// TESSERAX — point d'entrée : BOOTSTRAP MINCE (racine de composition).
//
// L'ancien monolithe IIFE (~4 600 lignes, @ts-nocheck) est découpé en modules typés
// (cf. docs/main-split-plan.md) ; il ne reste ici que l'ordre d'allumage :
//
//   state (état de jeu mutable partagé — importé par tous les modules)
//     └─ audio → scène → fx → render → hud → input → replay → menus → flow → rules → loop → boot
//
// Chaque module est une factory `createX(...)` ; les dépendances AVAL (règles pas encore
// créées quand input/replay/menus/flow se câblent) passent par des lambdas paresseuses —
// elles ne se déréférencent qu'à l'exécution, une fois tous les modules en place.
// Le cœur PUR (règles testées, RNG, score) vit dans src/core ; ici, rien que du câblage.
import { HEIGHT } from './core/constants';
import { initI18n } from './i18n';
import './styles.css';
import { state } from './game/state';
import { maxColHeight, holeCount, instabRatio, clamp01 } from './game/util';
import { createAudio } from './game/audio';
import { createScene } from './game/scene';
import { createFx } from './game/fx';
import { createRender } from './game/render';
import { createHud } from './game/hud';
import { createInput } from './game/input';
import { createReplay } from './game/replay';
import { createMenus } from './game/menus';
import { createFlow } from './game/flow';
import { createRules } from './game/rules';
import { createLoop } from './game/loop';
import { boot } from './game/boot';

(function () {
  'use strict';
  initI18n();   // détecte la langue (navigateur/localStorage) + applique les libellés statiques

  // Drapeaux d'URL / A-B (session seulement — cf. les commentaires historiques dans CLAUDE.md) :
  // ?zone=classic|bank (pouvoir Zone), ?pad=dpad|joy|touch (commande tactile, persistée),
  // ?admin=<clé> (panneau debug ; la clé VITE_ADMIN_KEY est publique par construction).
  try { const q = new URLSearchParams(location.search).get('zone');
    if (q === 'classic' || q === 'bank') state.ZONE_MODE = q;
    localStorage.removeItem('tesserax:zoneMode'); } catch (e) {}
  try { const q = new URLSearchParams(location.search).get('pad'), ls = localStorage.getItem('tesserax:padMode');
    if (q === 'joy' || q === 'dpad' || q === 'touch') state.PAD_MODE = q; else if (ls === 'joy' || ls === 'dpad' || ls === 'touch') state.PAD_MODE = ls; } catch (e) {}
  const _ADMIN_KEY = import.meta.env.VITE_ADMIN_KEY || '';
  if (_ADMIN_KEY) { try { const _ap = new URLSearchParams(location.search).get('admin');
    if (_ap && _ap === _ADMIN_KEY) { localStorage.setItem('tesserax:admin', '1'); state.adminMode = true; }
    else if (localStorage.getItem('tesserax:admin') === '1') state.adminMode = true; } catch (e) {} }

  // Préférences joueur (audio / qualité / accessibilité) — persistées, dégradation gracieuse.
  // Lues AVANT la création des modules : l'audio et la scène consomment ces valeurs via state.
  try { const g = (k: string) => localStorage.getItem(k); let v;
    if ((v = g('tesserax:enversdeep')) === '0') state.enversDeepPref = false;
    if ((v = g('tesserax:vol:music')) != null) state.musicVol = clamp01((+v) / 100);
    if ((v = g('tesserax:vol:sfx')) != null) state.sfxVol = clamp01((+v) / 100);
    if ((v = g('tesserax:shake')) === '0') state.shakePref = 0;
    else if (v == null && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) state.shakePref = 0; // accessibilité : honore la préférence OS par défaut (réglable ensuite)
    if ((v = g('tesserax:haptics')) === '0') state.hapticsPref = false;
    if ((v = g('tesserax:muted')) === '1') state.muted = true;   // AAC-AUDIO-SETTINGS-001 : mute lu AVEC les autres prefs ; la synchro des glyphes se fait au boot
    if ((v = g('tesserax:song')) === '0') state.songPref = false;
    if ((v = g('tesserax:world')) != null && /^([0-5]|auto)$/.test(v)) state.worldPref = v;
  } catch (e) {}

  // ── Audio : ne fait que LIRE l'état de jeu (getters) + 2 pulsations visuelles.
  const audio = createAudio({
    muted: () => state.muted, paused: () => state.paused, running: () => state.running,
    level: () => state.level, comboStreak: () => state.comboStreak, b2bCount: () => state.b2bCount, zoneActive: () => state.zoneActive,
    sfxVol: () => state.sfxVol, musicVol: () => state.musicVol, hapticsPref: () => state.hapticsPref, songPref: () => state.songPref,
    gpIndex: () => state.gpIndex,
    fill: () => maxColHeight() / HEIGHT, holes: () => holeCount(),
    instab: () => state.rightReady ? instabRatio() : 0,
    onBeat: (v) => { state.beatPulse = Math.max(state.beatPulse, v); },
    onSparkle: (star, node) => { state.starPulse = Math.max(state.starPulse, star); state.nodePulse = Math.max(state.nodePulse || 0, node); },
  });

  // ── Scène THREE (renderer, chambre tesseract, matériaux, pile instanciée).
  // WebGL absent → createScene affiche le message honnête et renvoie null : on interrompt le boot.
  const viz = createScene();
  if (!viz) return;
  // La qualité persistée s'applique une fois la scène créée (auto-détection sinon).
  try { const v = localStorage.getItem('tesserax:quality');
    if (v === '0' || v === '1' || v === '2') { state.qPref = v; viz.qLevel = +v; } } catch (e) {}

  // ── Modules du shell — les lambdas vers `rules`/`flow` sont PARESSEUSES (créés plus bas).
  const fx = createFx(viz);
  const render = createRender(viz, audio, fx, { missionToast: (txt, kind) => hud.missionToast(txt, kind) });
  const hud = createHud(viz, audio);
  const input = createInput(viz, audio, hud, {
    act: (n) => rules.act(n), move: (a, b, c) => rules.move(a, b, c), commitBascule: () => rules.commitBascule(),
    cancelBasculePreview: () => rules.cancelBasculePreview(), basculePreviewDir: (d) => rules.basculePreviewDir(d),
    strateOvDismiss: () => flow.strateOvDismiss(), toggleGhost: () => replayMod.toggleGhost(),
    togglePause: () => flow.togglePause(), unlockAudio: () => audio.unlockAudio(),
  });
  const replayMod = createReplay(viz, audio, render, hud, {
    startGame: (mo) => flow.startGame(mo), quitToMenu: () => flow.quitToMenu(),
    move: (a, b, c) => rules.move(a, b, c), rotate: (ax) => rules.rotate(ax), softDrop: () => rules.softDrop(), hardDrop: () => rules.hardDrop(),
    doHold: () => rules.doHold(), tryZone: () => rules.tryZone(), doBascule: (d) => rules.doBascule(d), doFold: () => rules.doFold(),
    acquireRelic: (r) => rules.acquireRelic(r), chooseDone: () => flow.chooseDone(),
  });
  const menus = createMenus(viz, audio, hud, {
    act: (n) => rules.act(n), startChallenge: (id) => flow.startChallenge(id), renderChallenges: () => flow.renderChallenges(),
    setWorld: (i, a) => rules.setWorld(i, a), relicGlyph: (r) => hud.relicGlyph(r), DAS_PRESETS: input.DAS_PRESETS,
  });
  const flow = createFlow(viz, audio, fx, render, hud, menus, replayMod, {
    act: (n) => rules.act(n), recIn: (c, a) => rules.recIn(c, a), frameCamera: () => loop.frameCamera(),
    isTouchPad: () => input.isTouchPad, isDpad: () => input.isDpad,
    spawn: () => rules.spawn(), fillQueue: () => rules.fillQueue(), refillBag: () => rules.refillBag(),
    setWorld: (i, a) => rules.setWorld(i, a), relicApi: () => rules.relicApi, emitRelic: (ev, p) => rules.emitRelic(ev, p),
    acquireRelic: (r) => rules.acquireRelic(r), checkDanger: () => rules.checkDanger(), applyGravity: () => rules.applyGravity(),
    doBascule: (d) => rules.doBascule(d), exitTwist: () => rules.exitTwist(),
  });
  const rules = createRules(viz, audio, fx, render, hud, input, replayMod, menus, flow);

  // ── Orbite/zoom SOURIS (desktop). Le tactile est géré par la couche caméra d'input.ts.
  const { sceneCanvas } = viz;
  let dragging = false, lx = 0, ly = 0;
  sceneCanvas.addEventListener('pointerdown', (e: PointerEvent) => { if (e.pointerType === 'touch') return; dragging = true; state.viewTweening = false; lx = e.clientX; ly = e.clientY; sceneCanvas.setPointerCapture(e.pointerId); });
  sceneCanvas.addEventListener('pointermove', (e: PointerEvent) => { if (e.pointerType === 'touch' || !dragging) return; state.camTheta -= (e.clientX - lx) * 0.006; state.camPhi -= (e.clientY - ly) * 0.006; state.camPhi = Math.max(0.18, Math.min(1.45, state.camPhi)); lx = e.clientX; ly = e.clientY; });
  sceneCanvas.addEventListener('pointerup', (e: PointerEvent) => { if (e.pointerType === 'touch') return; dragging = false; });
  sceneCanvas.addEventListener('pointercancel', (e: PointerEvent) => { if (e.pointerType === 'touch') return; dragging = false; });
  sceneCanvas.addEventListener('wheel', (e: WheelEvent) => { e.preventDefault(); state.viewTweening = false; state.camR *= (1 + Math.sign(e.deltaY) * 0.08); state.camR = Math.max(14, Math.min(64, state.camR)); }, { passive: false });

  // ── Boucle de rendu, séquence de démarrage, et c'est parti.
  const loop = createLoop(viz, audio, fx, render, hud, input, replayMod, menus, flow, rules, () => dragging);
  boot(viz, audio, fx, render, hud, input, replayMod, menus, flow, rules, loop);
  requestAnimationFrame(loop.animate);
})();
